<script setup lang="ts">
import { computed, onMounted, ref } from 'vue'
import HelpButton from '@/components/common/help/HelpButton.vue'
import MarkdownContent from '@/components/common/layout/MarkdownContent.vue'
import { useNewsStore } from '@/stores/useNewsStore'
import type { NewsType } from '@/stores/useNewsAdminStore'
import { NEWS_TYPE_BADGES, NEWS_TYPE_LABELS } from '@/utils/newsDisplay'
import { formatDateForDisplay } from '@/utils/dateUtils'

const store = useNewsStore()

const typeFilter = ref<NewsType | ''>('')

const hasMore = computed(() => store.items.length < store.total)

const formatDate = (iso: string) => formatDateForDisplay(new Date(iso))

async function applyFilter() {
  await store.fetchFeed({ type: typeFilter.value || null })
}

async function loadMore() {
  await store.fetchFeed({
    offset: store.items.length,
    type: typeFilter.value || null,
    append: true,
  })
}

onMounted(async () => {
  await store.fetchFeed()
  // Visiter le fil vaut accusé de lecture ; les indicateurs "nouveau" du
  // chargement courant restent affichés (readAt local non modifié).
  void store.markAllSeen()
})
</script>

<template>
  <div class="container mx-auto px-4 py-8 max-w-3xl">
    <div class="flex flex-col md:flex-row md:items-center justify-between gap-3 mb-8">
      <div class="flex items-center gap-1">
        <h1 class="text-3xl font-bold">Fil d'actualité</h1>
        <HelpButton />
      </div>
      <select v-model="typeFilter" class="select select-bordered select-sm w-44" @change="applyFilter">
        <option value="">Tous les types</option>
        <option value="FEATURE">Nouveautés</option>
        <option value="CHANGELOG">Changelog</option>
        <option value="INFO">Informations</option>
        <option value="ALERT">Alertes</option>
      </select>
    </div>

    <div v-if="store.isLoading && store.items.length === 0" class="py-12 text-center">
      <span class="loading loading-spinner loading-lg" />
    </div>

    <div v-else-if="store.items.length === 0" class="py-12 text-center text-base-content/60">
      Aucune actualité pour le moment.
    </div>

    <div v-else class="space-y-6">
      <div v-for="item in store.items" :key="item.uuid" class="card bg-base-100 shadow">
        <div class="card-body">
          <div class="flex flex-col md:flex-row justify-between items-start md:items-center gap-2 pb-3 border-b border-base-300">
            <h2 class="card-title text-lg font-bold flex items-center gap-2">
              <span class="badge" :class="NEWS_TYPE_BADGES[item.type]">{{ NEWS_TYPE_LABELS[item.type] }}</span>
              {{ item.title }}
              <span v-if="!item.readAt" class="badge badge-secondary badge-sm">nouveau</span>
            </h2>
            <div class="text-sm opacity-70 shrink-0">{{ formatDate(item.publishedAt) }}</div>
          </div>
          <MarkdownContent :content="item.body" class="mt-2" />
        </div>
      </div>

      <div v-if="hasMore" class="text-center">
        <button class="btn btn-outline btn-sm" :disabled="store.isLoading" @click="loadMore">
          <span v-if="store.isLoading" class="loading loading-spinner loading-xs" />
          Charger plus
        </button>
      </div>
    </div>
  </div>
</template>
